import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type ExecutionProgress = {
  totalControls?: number
  satisfiedControls?: number
  controlsSatisfiedLabel?: string
  openBlockerCount?: number
  missingEvidenceCount?: number
  assessmentPercent?: number | null
  staleEvidenceCount?: number
  coveragePercent?: number | null
}

export type ScopeAuthority = {
  selectedInformationSystemIds?: string[]
  scopeRevision?: number
  scopeStatus?: string
  finalizedAt?: string | null
  staleDiscovery?: boolean
  emptyScope?: boolean
  organizationWideWithoutSystems?: boolean
  mismatches?: Array<{ type: string; message?: string; systemName?: string; recommendation?: string }>
}

export type ExecutionContext = {
  orchestrationEnabled?: boolean
  standardCode: string
  activeStage?: string
  scopeAuthority?: ScopeAuthority | null
  progress?: ExecutionProgress
  resume?: {
    currentStage?: string
    discoveries?: unknown[]
    discoveryPersisted?: boolean
    shouldRerunDiscovery?: boolean
    nextActions?: Array<{ type: string; label: string; controlKey?: string }>
    openBlockerCount?: number
    scopeSummary?: { systemCount?: number; organizationWide?: boolean }
  }
  unresolvedBlockers?: Array<{ title: string; severity: string; description?: string }>
  lifecycleTimeline?: Array<{ key: string; status: string; labelKey?: string }>
  orchestrationNarratives?: string[]
  assessmentBindings?: Array<{
    controlKey: string
    title?: string
    questions?: unknown[]
    expectedEvidence?: Array<{ title: string }>
  }>
}

export async function fetchExecutionSessions() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_EXECUTION_SESSIONS), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as { sessions: Array<{ standardCode: string; currentStage?: string }>; enabledStandards?: string[] }
}

export async function fetchExecutionContext(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_EXECUTION_CONTEXT(standardCode)), {
    headers: getAuthHeaders(),
  })
  return parseJson(res) as ExecutionContext
}

export async function resumeExecutionSession(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_EXECUTION_RESUME(standardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res) as ExecutionContext
}

export async function recomputeExecutionContext(standardCode: string) {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.COMPLIANCE_EXECUTION_RECOMPUTE(standardCode)), {
    method: 'POST',
    headers: getAuthHeaders(),
  })
  return parseJson(res) as ExecutionContext
}

const STORAGE_KEY = 'defendsphere.execution.standard'

export function getPersistedExecutionStandard(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(STORAGE_KEY)
}

export function setPersistedExecutionStandard(code: string | null) {
  if (typeof window === 'undefined') return
  if (code) localStorage.setItem(STORAGE_KEY, code)
  else localStorage.removeItem(STORAGE_KEY)
}

export type ScopeDiagnostics = {
  standardCode: string
  scopeRevision?: number
  scopeStatus?: string
  workflowScopeSystems?: string[]
  applicabilitySystems?: string[]
  readinessSystems?: string[]
  blockerSystems?: string[]
  mismatches?: Array<{
    type: string
    systemId?: string
    systemName?: string
    source?: string
    recommendation?: string
  }>
  staleDiscovery?: boolean
}

export async function fetchScopeDiagnostics(standardCode: string): Promise<ScopeDiagnostics> {
  const res = await fetch(
    withOrganizationScope(API_ENDPOINTS.COMPLIANCE_EXECUTION_SCOPE_DIAGNOSTICS(standardCode)),
    { headers: getAuthHeaders() }
  )
  return parseJson(res)
}
